// @ts-strict-ignore

import { FunctionComponent, useMemo } from 'react';

import { observer } from 'mobx-react';
import moment from 'moment';
import { ticketSelectors } from 'selectors/ticket.selectors';

import { formatElapsedTime } from 'utils/DateUtils';
import { pluralize } from 'utils/StringUtils';

import { TicketType } from 'constants/TicketType';

import Patient from 'models/Patient';

import { TicketSectionId } from 'views/WorkQueue/WorkQueue.types';

import OverdueActions from 'components/Ticket/TicketRow/OverdueActions';
import { usePatientProviderLocation } from 'components/Ticket/TicketRow/shared';
import TicketRenderer from 'components/Ticket/TicketRow/TicketRenderer';

import TicketRowIcon, { TicketRowIconType } from './TicketRowIcon';


interface Props {
  patient: Patient;
  className?: string;
  toggleSmsModal: (shouldOpenSmsBlockedPopup: boolean, patient: Patient) => void;
  withPatientLink: boolean;
  ticketIndex: number;
  ticketSectionCurrentPage?: number;
}

const MINUTES_TILL_LAST_OVERDUE_NOTIFICATION = 30;

const OverduePatientReportRow: FunctionComponent<Props> = ({
  patient,
  className,
  toggleSmsModal,
  withPatientLink,
  ticketIndex,
  ticketSectionCurrentPage = 0
}) => {
  const providerLocationsText = usePatientProviderLocation(patient);
  const overdueTimeText = useMemo(() => {
    let duration = '';
    if (patient.hasDrugSpecificHourlyProtocol) {
      // TODO: Move logic to server. The server returns the start time of the last SMS reminder sent to the patient, but the overdue time starts 30 minutes before that.
      let actualStartTime;
      if (patient.isOverdueHospitalized) {
        actualStartTime = moment(patient.hospitalizationReportedAt);
      } else {
        actualStartTime = moment(patient.protocolOverdueStartTime).subtract(
          MINUTES_TILL_LAST_OVERDUE_NOTIFICATION,
          'minutes'
        );
      }
      actualStartTime = actualStartTime.toDate();

      duration = formatElapsedTime(actualStartTime);
    } else {
      const days = moment.utc().diff(moment(patient.protocolOverdueStartTime), 'days');
      if (days > 0) {
        duration = `${days} ${pluralize('Day', days)}`;
      } else {
        duration = 'Less than 1 day';
      }
    }

    let protocolText = 'Symptom Assessment';

    if (patient.drugSpecific) {
      protocolText = `${patient.drugSpecific.name}${
        patient.isOverdueHospitalized ? ' (Currently Hospitalized)' : ''
      }`;
    } else if (patient.hasOralProtocol) {
      protocolText = `Oral Oncolytics Assessment (${patient.regimenTitle})`;
    }

    return `${duration} Overdue: ${protocolText}`;
  }, [
    patient.hasDrugSpecificHourlyProtocol,
    patient.drugSpecific,
    patient.hasOralProtocol,
    patient.protocolOverdueStartTime,
    patient.isOverdueHospitalized,
    patient.regimenTitle,
    patient.hospitalizationReportedAt
  ]);

  const icon = useMemo(() => {
    if (patient.hasDrugSpecificHourlyProtocol && !patient.isOverdueHospitalized) {
      return <TicketRowIcon type={TicketRowIconType.SYMPTOM_REPORT_IMMEDIATE_ATTENTION} />;
    }
    return null;
  }, [patient.hasDrugSpecificHourlyProtocol, patient.isOverdueHospitalized]);

  const summary = (
    <div>
      <span className="title-text bold">{overdueTimeText}</span>
      {providerLocationsText && <span className="subtitle-text"> — {providerLocationsText}</span>}
    </div>
  );

  return (
    <TicketRenderer
      testHook={ticketSelectors.container(
        patient.id,
        TicketType.Overdue,
        TicketSectionId.OverdueReports
      )}
      dashedBorder
      patient={patient}
      icon={icon}
      classes={{
        container: className
      }}
      actions={
        <OverdueActions
          patient={patient}
          toggleSmsModal={toggleSmsModal}
          ticketIndex={ticketIndex}
          ticketSectionCurrentPage={ticketSectionCurrentPage}
        />
      }
      summary={summary}
      withPatientLink={withPatientLink}
      createdByText={
        <>{patient.mrn && <div className="bold patient-mrn details-row">MRN: {patient.mrn}</div>}</>
      }
    />
  );
};

export default observer(OverduePatientReportRow);
